import { word } from "@modules/core/utils/i18n"
import {
  ProgramBundleNoUnfollowModal,
  ProgramBundleNoUnfollowModalRef,
} from "@modules/programBundle/components/programBundleNoUnfollowModal"
import {
  ProgramBundlePauseOnOffModal,
  ProgramBundlePauseOnOffModalRef,
} from "@modules/programBundle/components/programBundlePauseOnOffModal"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { useProgramOnOffProgression } from "@modules/programs/hooks/useProgramsOnOff"
import { ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOffTypeOf, ProgramProgressionStatus } from "@modules/programs/programOnOffTypes"
import { ProgramSubscriptionStatus } from "@modules/programs/programTypes"
import { HuProgramMiniCards } from "@modules/ui/components/cards/huProgramMiniCard"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { getApproximatedFormattedNumber } from "@modules/utils/numberUtils"
import { UseMutateAsyncFunction } from "@tanstack/react-query"
import { Check, PlusIcon } from "lucide-react"
import { useMemo, useRef, useState } from "react"
import { useTheme } from "styled-components"

type HuProgramBundleMiniCardProps = {
  programId: string
  programType: typeof ProgramInfiniteTypeOf | typeof ProgramOnOffTypeOf
  canUserFollowBundlePrograms: boolean
  followProgamsInProgramBundle: UseMutateAsyncFunction<void, Error, string[], unknown>
  unFollowProgamsInProgramBundle: UseMutateAsyncFunction<void, Error, string[], unknown>
  isFollowed: boolean | undefined
} & React.HTMLAttributes<HTMLDivElement>

export const HuProgramBundleMiniCard: React.FC<HuProgramBundleMiniCardProps> = ({
  programId,
  programType,
  canUserFollowBundlePrograms,
  followProgamsInProgramBundle,
  unFollowProgamsInProgramBundle,
  isFollowed,
  ...props
}) => {
  const [isPending, setIsPending] = useState(false)

  const programBundleNoUnfollowModalRef = useRef<ProgramBundleNoUnfollowModalRef>(null)
  const programBundlePauseOnOffModalRef = useRef<ProgramBundlePauseOnOffModalRef>(null)

  const theme = useTheme()

  const defaultClass = "flex w-full bg-neutralColor-100 border-round-3xl"

  const { result: program, loading: isLoadingProgram } = useProgram(programId, programType) // Consuming query to get the program subscription status
  const { approximatedFormattedNumber, approximatedNumber } = getApproximatedFormattedNumber(
    program?.subscriptionCount ?? 0,
  )

  const { data: progression } = useProgramOnOffProgression(
    program?.id,
    program
      ? program._programType !== ProgramOnOffTypeOf ||
          !(
            program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
            program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE_BUNDLE ||
            program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE_BUNDLE ||
            program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION
          )
      : false,
  )

  const isSubscribedInBothProgramAndBundle = useMemo(
    () =>
      // considering that this program is in the bundle
      canUserFollowBundlePrograms && program && program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE,
    [isLoadingProgram, program],
  )

  const handleClick = async () => {
    setIsPending(true)
    if (isSubscribedInBothProgramAndBundle) {
      // Tell the user that he can't unfollow the program because he is subscribed to it individually
      programBundleNoUnfollowModalRef.current?.open()
    } else if (
      isFollowed &&
      program?._programType === ProgramOnOffTypeOf &&
      progression &&
      progression.status === ProgramProgressionStatus.STARTED
    ) {
      // Tell the user that his progression will be paused if he unfollows the program
      programBundlePauseOnOffModalRef.current?.open()
    } else if (isFollowed) {
      await unFollowProgamsInProgramBundle([programId])
    } else {
      await followProgamsInProgramBundle([programId])
    }
    setIsPending(false)
  }

  if (!program) return null

  return (
    <div className={props.className ?? defaultClass} {...props}>
      <HuProgramMiniCards item={program} className="w-9rem h-9rem mx-0" minWidth="9rem" editionAllowed />
      <div className="flex flex-grow-1 flex-column gap-2 mx-4 my-2 ">
        <div className="flex justify-content-between align-items-center">
          {canUserFollowBundlePrograms && isFollowed === true ? (
            <HUButton
              type="Left_Icon"
              colorType="Primary"
              size="L"
              text={word("programBundle.follow.remove.button")}
              icon={{ iconView: <Check color={theme.mainBlack} width={24} height={24} /> }}
              className="px-4 py-2"
              onClick={() => handleClick()}
              disabled={isPending}
            />
          ) : canUserFollowBundlePrograms && isFollowed === false ? (
            <HUButton
              type="Left_Icon"
              colorType="Tertiary"
              size="L"
              text={word("programBundle.follow.add.button")}
              icon={{ iconView: <PlusIcon color={theme.mainBlack} width={24} height={24} /> }}
              className="px-4 py-2"
              onClick={() => handleClick()}
              disabled={isPending}
            />
          ) : (
            <HUText fontStyle="LM" color={theme.neutralColor700} className="flex">
              {word("programBundle.info.programs.by", {
                firstname: program.author.firstname,
                lastname: program.author.lastname,
              })}
            </HUText>
          )}
          <div className="flex gap-1 align-items-baseline">
            <HUText fontStyle="LL" color={theme.neutralColor900} className="flex ">
              {approximatedFormattedNumber}
            </HUText>
            <HUText fontStyle="BS" color={theme.neutralColor900} className="flex ">
              {word(program._programType === ProgramOnOffTypeOf ? "global.athlete" : "program.subcribers.name", {
                count: approximatedNumber !== 0 ? approximatedNumber : 1,
              })}
            </HUText>
          </div>
        </div>
        <HUText fontStyle="BM" numberOfLine={4} color={theme.neutralColor900}>
          {program.description}
        </HUText>
      </div>

      <ProgramBundleNoUnfollowModal ref={programBundleNoUnfollowModalRef} programType={program._programType} />
      <ProgramBundlePauseOnOffModal
        ref={programBundlePauseOnOffModalRef}
        onConfirmUnfollow={() => unFollowProgamsInProgramBundle([program.id])}
      />
    </div>
  )
}
